---
title: "RSI, MACD and Stochastic: Momentum Indicators Explained"
slug: rsi-macd-stochastic
shortTitle: "RSI, MACD & Stochastic"
description: "What RSI, MACD and the stochastic oscillator measure, how each is calculated, what overbought, crossovers and divergence really mean, and their limits."
level: intermediate
category: trading
course: intermediate
order: 4
topics: [indicators]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/rsi-macd-stochastic.svg
faqs:
  - q: "Does an RSI above 70 mean I should sell?"
    a: "No. A reading above 70 means recent gains have far outweighed recent losses. In a strong uptrend the RSI can stay above 70 for a long time while price keeps rising, so many traders read it as a sign of strength rather than a reason to sell."
  - q: "Which momentum indicator is the best?"
    a: "None is best in general. RSI and the stochastic are bounded oscillators that suit spotting stretched conditions, while MACD is better at showing shifts in trend momentum. Learn one well before adding another."
  - q: "Should I use RSI, MACD and the stochastic together?"
    a: "You can, but they are all built from the same price data, so three agreeing readings are not three independent confirmations. Many traders pair one momentum indicator with something different, such as support and resistance or a moving average."
  - q: "What settings should I use?"
    a: "The common defaults are RSI 14, MACD 12, 26 and 9, and stochastic 14, 3 and 3. Shorter settings react faster and give more signals, many of them false; longer settings are smoother and slower. Most traders keep the defaults so their readings match what others see."
  - q: "Where can I see these indicators on the platform?"
    a: "The trade chart does not currently plot indicators. RSI readings and a MACD reading appear in the Markets page quick view, and the opportunity scanner can list markets that are overbought or oversold on RSI. For a full indicator panel, traders can use a separate charting tool."
---

A moving average tells you which way price has been going. **Momentum indicators** tell you how hard it has been pushing. They measure the speed of recent price changes, so they can show a trend gaining strength, running out of steam, or stretched further than usual. Three of the most popular are the **RSI**, **MACD** and the **stochastic oscillator**.

This lesson explains what each one measures, how it is calculated, the readings traders watch, and the traps that catch people who treat those readings as instructions. None of them predicts the future. They are different ways of summarizing price that has already printed.

<CTA label="See RSI readings on Markets" to="/markets" />

<KeyTakeaways>

- **Momentum is speed, not direction.** These tools measure how fast and how consistently price has moved over a set number of candles.
- **RSI and stochastic are bounded.** Both run from 0 to 100, with zones commonly called overbought (high) and oversold (low).
- **MACD tracks two moving averages.** The gap between a fast and a slow EMA, and that gap's own signal line, show momentum building or fading.
- **Overbought is not a sell signal.** Strong trends keep oscillators at extremes for long periods; read them with the trend and price levels, not against them.

</KeyTakeaways>

## RSI: the Relative Strength Index

The **RSI** compares the size of recent up-moves with recent down-moves, usually over 14 candles, and scales the result from 0 to 100.

1. Work out the **average gain** and **average loss** over the period (losses as positive numbers).
2. Divide them: **RS = average gain ÷ average loss**.
3. Convert: **RSI = 100 − 100 ÷ (1 + RS)**.

<Example title="RSI from average gains and losses (illustrative numbers)">

Over the last 14 hourly candles on ETH/USDT, the average gain is 30 USDT and the average loss is 15 USDT.

- RS = 30 ÷ 15 = 2
- RSI = 100 − 100 ÷ (1 + 2) = 100 − 33.3 = **66.7**

If instead the average gain were 10 and the average loss 30, RS would be 0.33 and RSI = 100 − 100 ÷ 1.33 = **25**.

</Example>

Readings at or above **70** are commonly called **overbought**, and at or below **30** **oversold**. Those labels only mean that one side has dominated recently. In a strong trend, RSI often spends long stretches in these zones. Many trend traders instead watch the 40–50 area in an uptrend, or 50–60 in a downtrend, as signs that a pullback has cooled off.

## MACD: Moving Average Convergence Divergence

**MACD** is built from exponential moving averages, explained in [Moving Averages (SMA/EMA)](/academy/article/moving-averages). The standard settings are 12, 26 and 9:

- **MACD line** = 12-period EMA − 26-period EMA
- **Signal line** = 9-period EMA of the MACD line
- **Histogram** = MACD line − signal line

When the fast EMA pulls away from the slow one, the MACD line moves away from zero: momentum is building. When the two converge, it heads back towards zero.

<Example title="Reading MACD on BTC/USDT (illustrative prices)">

On a 4-hour chart, the 12-period EMA is 60,600 and the 26-period EMA is 60,200. The signal line is 320.

- MACD line: 60,600 − 60,200 = **400**
- Histogram: 400 − 320 = **+80**

A few candles later the MACD line is 350 and the signal line 340, so the histogram is just +10. Both lines are still above zero, but the shrinking histogram shows the upward push fading.

</Example>

Traders watch three things: the MACD line crossing its signal line, the MACD line crossing zero (the two EMAs crossing each other), and the histogram growing or shrinking. MACD has no fixed upper or lower bound, so "overbought" does not apply to it.

## The stochastic oscillator

The **stochastic** asks where the latest close sits within the recent high-low range. A close near the top of the range scores near 100; near the bottom, near 0.

- **%K** = (close − lowest low) ÷ (highest high − lowest low) × 100, usually over 14 candles
- **%D** = a 3-period average of %K, used as a signal line

<Example title="Stochastic on SOL/USDT (illustrative prices)">

Over the last 14 daily candles, the highest high is 160 and the lowest low 140. Today closes at 156.

- %K = (156 − 140) ÷ (160 − 140) × 100 = 16 ÷ 20 × 100 = **80**
- If the last three %K values were 80, 74 and 71, then %D = (80 + 74 + 71) ÷ 3 = **75**

</Example>

Readings above **80** are commonly called overbought and below **20** oversold. Because the stochastic reacts to every close inside a short range, it moves faster than RSI and gives more signals, including more false ones.

<CompareTable caption="RSI, MACD and stochastic compared">

| | RSI | MACD | Stochastic |
| --- | --- | --- | --- |
| Measures | Size of gains versus losses | Gap between a fast and a slow EMA | Close versus the recent range |
| Scale | 0 to 100 | Unbounded, around zero | 0 to 100 |
| Common settings | 14 | 12, 26, 9 | 14, 3, 3 |
| Levels watched | 70 and 30, plus 50 | Zero line and signal line | 80 and 20 |
| Weak spot | Stays extreme in strong trends | Lags; choppy near zero | Very jumpy in ranges |

</CompareTable>

## Divergence: when price and momentum disagree

**Divergence** is when price and an indicator point different ways. If price makes a higher high but RSI makes a lower high, the second push up had less force behind it; that is a **bearish divergence**. A lower low in price with a higher low in the indicator is a **bullish divergence**.

Divergence describes weakening momentum. It does not tell you when, or whether, price will turn, and in strong trends it can appear several times before anything changes. Traders who use it usually wait for price itself to confirm, for example with a break of a recent swing low, before acting.

<Warning>

Selling a market just because RSI or the stochastic reads "overbought" means betting against the trend at the moment it is strongest. With leverage, a trend that keeps going can push such a trade into a large loss quickly. Use oscillator readings as context, and always define your stop loss and position size before you open a trade.

</Warning>

## Using momentum indicators with context

The indicators work best as part of a short checklist rather than as triggers.

<Steps>
<Step title="Start with the trend">

Decide the direction on a higher timeframe first, as described in [Multi-Timeframe Analysis](/academy/article/multi-timeframe-analysis). It tells you which momentum readings matter.

</Step>
<Step title="Look for momentum in the trend's direction">

In an uptrend, a pullback that drags RSI back towards 40–50 or turns the MACD histogram up again is more relevant than an "overbought" reading.

</Step>
<Step title="Confirm with price">

Look for price to react at a level or form a clear candle before acting. The indicator is the supporting evidence, not the reason.

</Step>
<Step title="Set your risk first">

Place your stop beyond the swing that would prove you wrong, size the trade from that distance, and enter both with the **TP / SL** switch.

</Step>
</Steps>

<ProTip>

RSI, MACD and the stochastic are all calculated from the same closing prices. When all three agree, you have not found three confirmations, you have measured one thing three times. Pair a single momentum tool with something different, such as a support level or a volume reading.

</ProTip>

## Momentum readings on {{BRAND}}

The chart on the trade screen does not currently plot indicators. Momentum readings live on the **Markets** page:

- The **quick view** shows **RSI (14, 1h)** in its stats, and its **Daily trend** section shows **RSI (14, daily)** and whether the **MACD (12, 26, 9)** line is above or below its signal line.
- The **Opportunity scanner** lists markets under **Oversold** and **Overbought** (RSI 14 on hourly candles at or beyond 30 and 70) and **Momentum**. It labels itself as screens, not advice.

{/* TODO: verify with platform — trade chart has no indicators; RSI and MACD labels in the quick view, scanner thresholds; no stochastic reading on the platform. */}

The stochastic is not shown on the platform. If you want full indicator panels, you can use a separate charting tool for analysis and place your orders on {{BRAND}}, checking that both show the same market and timeframe. [Volume and Volatility](/academy/article/volume-and-volatility) covers the other family of indicators worth knowing.

## Conclusion

RSI compares recent gains with recent losses, MACD tracks the gap between two EMAs, and the stochastic places the close within the recent range. All three measure momentum: how hard price has been pushing, not where it will go. Overbought and oversold zones describe stretched conditions that strong trends can sustain, and divergence warns of fading force without timing a turn. Read them in the direction of the trend, confirm with price, and keep your stop loss and position size fixed before every trade.
